import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

// Utils
import {
	filterErrorsList,
	getCombinedBonuses,
	getErrorsToRemove,
	getPotentialTabsErrorsKeys,
	getVolumeBonusTabs,
	isSomeConfiguredModel,
	mapDefinitionsToArray,
	mapModelToPropagate,
	resizeBonuses,
	setPeriphery,
	synchronizePeriphery,
} from "./utils";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { getErrorKey } from "@/Common/helpers/Modifiers/characters";
import { removeListValue } from "@/Common/helpers/Arrays";
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { getMatrixDimensions, getExactMatrixDimensions, getSmallestBonusMatrixDimensions } from "@/Common/helpers/Matrices";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";
import TabLoader from "@/Layouts/components/Skeletons/TabLoader";
import TabSelectable from "@/Layouts/components/Tab/TabSelectable";

// Custom components
import VolumeBonus from "../VolumeBonus";

// MUI
import { Grid } from "@mui/material";

export default function VolumeBonuses(props) {
	const {
		sectionId = "volumeBonus",
		position = "",
		content = {
			bonuses: [],
			preOwned: null,
			newAndPreOwnedRequired: false,
			combineFranchises: false,
			globalUnitCounts: false,
		},
		franchises = [],
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Hooks
	const { errors, addSectionError, addSectionErrors, removeSectionError, removeSectionErrors, clearSectionErrors } =
		useSectionsErrors?.() || {};

	// States
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const [isValidModels, setIsValidModels] = useState(true);
	const [model, setModel] = useState(null);
	const [reputationStatus, setReputationStatus] = useState({
		sections: [],
		combined: false,
	});
	const [tabIndex, setTabIndex] = useState(0);
	const combinedTabIsSelected = model?.tabsProps?.tabs?.[tabIndex] === "Combined";

	const preventSave = useRef([]);
	const componentErrors = useRef({});

	const matrixDimensions = getMatrixDimensions("bonuses", model?.bonuses || []);

	// Error handler
	const tabsErrors = useMemo(() => {
		if (errors.size && model?.tabsProps?.tabs) {
			const localErrors = Array.from(Array(model.tabsProps.tabs.length)).map(() => false);
			const errorsToKeep = getPotentialTabsErrorsKeys(sectionId, model.tabsProps.tabs);
			const listErrors = errors?.get(sectionId) || []; // Ids that have errors

			errorsToKeep.forEach((errorKey, index) => {
				const included = listErrors.includes(errorKey) || preventSave?.current?.includes(errorKey);
				localErrors[index] = Boolean(included);
			});

			return localErrors;
		}
		return [];
	}, [sectionId, errors, model?.tabsProps?.tabs]);

	const onTabChange = (index) => {
		setTabIndex(index);
	};

	const updateComponentErrors = (bonuses) => {
		if (bonuses?.length) {
			const isSomeConfigured = isSomeConfiguredModel({ makes: bonuses });
			componentErrors.current = bonuses
				.filter((item) => item)
				.reduce((acc, { make }) => {
					return {
						...acc,
						[make]: !isSomeConfigured,
					};
				}, {});
		}
	};

	// Actions handlers
	const update = (section, data, area) => {
		const filteredErrors = filterErrorsList(errors?.get(section) || [], [
			getErrorKey(section, "component"),
			getErrorKey(section, area),
		]);
		let isValid = !filteredErrors.length;
		const newAndPreOwnedRequired = Boolean(data?.tabsProps?.newAndPreOwnedRequired);
		if (newAndPreOwnedRequired && isValid) {
			const columns = data?.bonuses?.[0]?.tiers?.[0]?.bonuses?.length;
			const tiers = data?.bonuses?.[0]?.tiers?.length;
			data?.bonuses?.forEach((bonus) => {
				if (bonus?.tiers?.[0]?.bonuses?.length !== columns || bonus?.tiers?.length !== tiers) {
					isValid = false;
				}
			});
		}

		isValid = data?.bonuses?.some((bonus) => bonus.tiers.length > 1 || bonus.tiers[0].bonuses.length > 1);
		if (isValid && !preventSave?.current?.length) {
			removeSectionError(section, "component");
			const modelToPropagate = mapModelToPropagate(data.bonuses, data.tabsProps);
			onActionUpdate?.(section, null, modelToPropagate, isValid);
		} else {
			setModel(clone(data));
		}
	};

	const handleUpdateTiers = (section, area, data) => {
		const updatedModel = clone(model);
		const { tiers, lowerTiers } = data || {};
		let bonuses = [...updatedModel.bonuses];

		const currentMatrixDimensions = getExactMatrixDimensions("bonuses", tiers);
		matrixDimensions.set(area, currentMatrixDimensions);
		preventSave.current = removeListValue(preventSave.current, getErrorKey(sectionId, area));
		removeSectionError(section, area);

		// if newAndPreOwned and globalUnits values of commissions must be equal across tabs
		if (model?.tabsProps?.newAndPreOwnedRequired && model?.tabsProps?.globalUnitCounts) {
			bonuses = setPeriphery(bonuses, tiers);
		}

		// Search make and update
		bonuses?.forEach((element, key) => {
			if (element.make === area) {
				bonuses[key]["tiers"] = tiers;
			}
		});

		if (model?.tabsProps?.newAndPreOwnedRequired && area !== "Combined") {
			updatedModel["bonuses"] = resizeBonuses({
				bonuses,
				preventSave,
				sectionId,
				newMatrixDimensions: currentMatrixDimensions,
				expand: true,
				shrink: true,
			});
		}

		for (const bonus of updatedModel.bonuses) {
			if (bonus.make === area) {
				bonus.subjectToReputation = lowerTiers.subjectToReputation;
				bonus.forgiveness = lowerTiers.forgiveness;
				bonus.adjustments = lowerTiers.adjustments;
			}
		}

		update(section, updatedModel, area);
	};

	const selectCorrectTab = (combineFranchisesSwitchState) => {
		const preOwnedTabActive = model?.bonuses?.[tabIndex]?.make === "Pre-Owned Vehicles";
		if (combineFranchisesSwitchState) {
			if (preOwnedTabActive) {
				setTabIndex(tabIndex - 1);
			} else {
				setTabIndex(0);
			}
		} else if (preOwnedTabActive) {
			setTabIndex(model.bonuses.length - 1);
		} else {
			setTabIndex(0);
		}
	};

	const handleUpdateSwitches = (section, area, value, currentTabIndex = null) => {
		const updatedModel = { ...model };

		// Closure to combine franchises
		const setCombineFranchises = (combine = false) => {
			const bonuses = getCombinedBonuses({
				bonuses: updatedModel["bonuses"],
				combineFranchises: combine,
				franchises,
				content,
				position,
			});

			const tabsProps = { ...updatedModel["tabsProps"], tabs: getVolumeBonusTabs([...bonuses]) };
			updatedModel["tabsProps"] = tabsProps;
			updatedModel["bonuses"] = bonuses;
			resetTabsErrors(section, updatedModel["tabsProps"]["tabs"]);
			updateComponentErrors(bonuses);
		};

		if (area === "applyTiersRetroactively" && isNumeric(currentTabIndex)) {
			updatedModel["bonuses"][currentTabIndex][area] = value;
		} else {
			updatedModel["tabsProps"][area] = value;
			if (area === "globalUnitCounts" && Boolean(updatedModel["tabsProps"][area])) {
				updatedModel["tabsProps"]["combineFranchises"] = true;
				setCombineFranchises(true);
			}
		}

		const combineFranchises = updatedModel["tabsProps"]["combineFranchises"];

		switch (area) {
			case "combineFranchises": {
				setCombineFranchises(combineFranchises);
				selectCorrectTab(combineFranchises);
				break;
			}
			case "newAndPreOwnedRequired":
				if (franchises?.length > 1 && value) {
					updatedModel["tabsProps"]["combineFranchises"] = true;
					updatedModel["tabsProps"]["globalUnitCounts"] = false;
				}

				// Combine franchises
				if (value) {
					const bonuses = getCombinedBonuses({
						franchises,
						content,
						position,
						bonuses: updatedModel["bonuses"],
						combineFranchises: updatedModel["tabsProps"]["combineFranchises"],
					});
					const currentTabsProps = { ...updatedModel["tabsProps"], tabs: getVolumeBonusTabs([...bonuses]) };
					updatedModel["tabsProps"] = currentTabsProps;
					const newDimensionsForAllMatrices = getSmallestBonusMatrixDimensions(bonuses);
					const shrunk = resizeBonuses({
						preventSave,
						getErrorKey,
						sectionId,
						bonuses: [...bonuses],
						newMatrixDimensions: {
							columns: newDimensionsForAllMatrices?.minColumns,
							tiers: newDimensionsForAllMatrices?.minTiers,
						},
						expand: false,
						shrink: true,
					});
					updatedModel["bonuses"] = [...shrunk];
					resetTabsErrors(section, updatedModel["tabsProps"]["tabs"]);
					// synchronize tabs after shrinking (if needed)
					synchronizePeriphery(updatedModel, currentTabIndex);
					updateComponentErrors([...shrunk]);
					selectCorrectTab(updatedModel["tabsProps"]["combineFranchises"]);
				}
				break;
			case "globalUnitCounts":
				synchronizePeriphery(updatedModel, currentTabIndex);
				break;
			default:
				break;
		}

		update(section, updatedModel, area);
	};

	const handleValidation = (section, area, value) => {
		value ? removeSectionError(section, area) : addSectionError(section, area);
	};

	const resetTabsErrors = useCallback(
		(currentSectionId, tabs) => {
			if (tabs?.length) {
				const errorsToKeep = getPotentialTabsErrorsKeys(currentSectionId, tabs);
				const errorsToRemove = getErrorsToRemove(errors?.get(currentSectionId), errorsToKeep);
				removeSectionErrors(currentSectionId, errorsToRemove);
				addSectionErrors(currentSectionId, errorsToKeep);
			}
		},
		[errors, removeSectionErrors, addSectionErrors]
	);

	useMemo(() => {
		const sectionErrors = errors?.get(sectionId) || [];
		setIsValidModels(!sectionErrors?.length);
	}, [sectionId, errors]);

	useEffect(() => {
		const additionalBonuses = mapDefinitionsToArray(content);
		const contentBonuses = content?.bonuses || [];
		const initialBonuses = [...contentBonuses, ...additionalBonuses];
		const bonuses = clone([...initialBonuses]);

		const modelUpdated = {
			tabsProps: {
				tabs: getVolumeBonusTabs([...bonuses]),
				newAndPreOwnedRequired: Boolean(content.newAndPreOwnedRequired),
				combineFranchises: Boolean(content.combineFranchises),
				globalUnitCounts: Boolean(content.globalUnitCounts),
				errors: [],
			},
			bonuses,
		};

		setModel(modelUpdated);
		updateComponentErrors(bonuses);

		return () => {
			setModel(null);
			preventSave.current = [];
		};
	}, [content]);

	useEffect(() => {
		return () => {
			clearSectionErrors(sectionId);
			setIsValidModels(true);
			setReputationStatus({
				sections: [],
				combined: false,
			});
		};
	}, [sectionId, clearSectionErrors]);

	const mandatoryDisabledSwitch = readOnly || !franchises?.length || combinedTabIsSelected;

	return (
		<Grid container>
			<Grid item xs={12} mt={2.5}>
				{loading ? (
					<Grid>
						<TabLoader switch bottomSwitch />
						<DefaultTiersLoader mt={1} buttons />
					</Grid>
				) : (
					Boolean(model?.bonuses?.length) && (
						<TabSelectable
							tabs={model?.tabsProps.tabs?.filter((tab) => franchises?.length || tab !== "Combined") || []}
							tabSelected={tabIndex}
							onTabChange={onTabChange}
							errors={tabsErrors}
							content={Object.entries(model?.bonuses)?.map(([key, value], index) => (
								<Grid container key={`${key}_${index}`}>
									<Grid item xs={12} className={"models-tiered-area"}>
										<VolumeBonus
											{...value}
											position={position}
											readOnly={readOnly}
											componentErrors={componentErrors}
											reputationStatuses={{ reputationStatus, setReputationStatus }}
											onActionUpdate={(make, tiers) => handleUpdateTiers(sectionId, make, tiers)}
											onActionValidation={(make, isValid) => handleValidation(sectionId, make, isValid)}
											newAndPreOwnedRequired={model?.tabsProps?.newAndPreOwnedRequired}
										/>
									</Grid>
								</Grid>
							))}
							topActions={[
								<CustomizedSwitches
									key={"newAndPreOwnedRequired"}
									textLeft={true}
									textChecked={"New and Pre-Owned Required"}
									textUnChecked={"New and Pre-Owned Required"}
									checked={Boolean(model?.tabsProps?.newAndPreOwnedRequired)}
									disabled={mandatoryDisabledSwitch}
									onChange={(value) => handleUpdateSwitches(sectionId, "newAndPreOwnedRequired", value)}
									tooltip={{
										title: "Combines franchises if applicable and makes all tabs share the same number of rows and columns.",
										placement: "bottom",
										key: "volume-bonus-new-and-preOwned-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"combineFranchises"}
									textLeft={true}
									textChecked={"Combine Franchises"}
									textUnChecked={"Combine Franchises"}
									checked={Boolean(model?.tabsProps?.combineFranchises)}
									disabled={
										mandatoryDisabledSwitch ||
										Boolean(model?.tabsProps?.newAndPreOwnedRequired) ||
										Boolean(model?.tabsProps?.globalUnitCounts) ||
										franchises?.length < 2
									}
									onChange={(value) => handleUpdateSwitches(sectionId, "combineFranchises", value)}
									tooltip={{
										title: 'Turns all franchise tabs into a "New Vehicles" tab.',
										placement: "bottom",
										key: "volume-bonus-combine-franchises-tooltip",
									}}
								/>,
								<CustomizedSwitches
									key={"globalUnitCounts"}
									textLeft={true}
									textChecked={"Global unit counts"}
									textUnChecked={"Global unit counts"}
									checked={Boolean(model?.tabsProps?.globalUnitCounts)}
									disabled={mandatoryDisabledSwitch || Boolean(model?.tabsProps?.newAndPreOwnedRequired)}
									onChange={(value) => handleUpdateSwitches(sectionId, "globalUnitCounts", value, tabIndex)}
									tooltip={{
										title: "Uses all units of New and Pre-Owned Vehicles together.",
										placement: "bottom",
										key: "volume-bonus-global-unit-counts-tooltip",
									}}
								/>,
							]}
							bottomActions={[
								<CustomizedSwitches
									key={"applyTiersRetroactively"}
									textLeft={true}
									textChecked={"Apply retroactively"}
									textUnChecked={"Apply retroactively"}
									checked={Boolean(model?.bonuses?.[tabIndex]?.applyTiersRetroactively)}
									disabled={readOnly}
									onChange={(value) => handleUpdateSwitches(sectionId, "applyTiersRetroactively", value, tabIndex)}
								/>,
							]}
						/>
					)
				)}
			</Grid>
		</Grid>
	);
}
VolumeBonuses.propTypes = {
	sectionId: PropTypes.string,
	position: PropTypes.string,
	content: PropTypes.object,
	franchises: PropTypes.array,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	useSectionsErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
};
